import ApiConstant from '../../constants/api.constants';
import api from '../api';

export interface NewsManagementDTO {

  ID?: number;
  title: string;
  newsDescription: string;
  description?: string; 
  imageFileName?: string;
  imageUrl?: string;
  containerName?: string;
  isActive: boolean;
  createdBy?: number;
  createdByName?: string;
  createdDate?: string;
  modifiedBy?: number;
  modifiedByName?: string;
  modifiedDate?: string;
}

export interface CreateNewsRequest {
  title: string;
  newsDescription: string;
  imageFile?: File;
}

export interface UpdateNewsRequest {
  ID: number;
  title: string;
  newsDescription: string;
  imageFile?: File;
}

export interface ApiResponse<T> {
  isSuccess: boolean;
  message: string;
  data?: T;
  id?: number;
  ID?: number;
}

class NewsManagementService {
  async getAllNews(): Promise<NewsManagementDTO[]> {
    try {
      const response = await api.get(ApiConstant.getNews);

      if (response && response.data) {
        const newsItems = Array.isArray(response.data) ? response.data : [response.data];
        return newsItems.map((item: any) => ({
        ID: item.ID ?? item.Id ?? item.id,
          title: item.title,
          newsDescription: item.newsDescription,
          description: item.newsDescription, 
          imageFileName: item.imageFileName,
          imageUrl: item.imageUrl,
          containerName: item.containerName,
          isActive: item.isActive,
          createdBy: item.createdBy,
          createdByName: item.createdByName,
          createdDate: item.createdDate,
          modifiedBy: item.modifiedBy,
          modifiedByName: item.modifiedByName,
          modifiedDate: item.modifiedDate,
        }));
      }

      return [];
    } catch (error) {
      console.error('Failed to fetch all news:', error);
      throw error;
    }
  }

  /**
   * Get single news by ID
   */
  async getNewsById(newsId: number): Promise<NewsManagementDTO | null> {
    try {
      const allNews = await this.getAllNews();
      return allNews.find(n => n.ID === newsId || n.ID === newsId) || null;
    } catch (error) {
      console.error(`Failed to fetch news with ID ${newsId}:`, error);
      throw error;
    }
  }

  /**
   * Create new news
   * Uses FormData for file upload
   */
  async createNews(newsData: CreateNewsRequest): Promise<ApiResponse<any>> {
    try {
      const formData = new FormData();
      formData.append('title', newsData.title);
      formData.append('newsDescription', newsData.newsDescription);

      if (newsData.imageFile) {
        formData.append('imageFile', newsData.imageFile);
      }

      const response = await api.post(ApiConstant.createNews, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return {
        isSuccess: response.data.isSuccess,
        message: response.data.message,
        id: response.data.ID || response.data.id,
        ID: response.data.ID,
        data: response.data,
      };
    } catch (error: any) {
      console.error('Failed to create news:', error);
      throw error;
    }
  }

  /**
   * Update existing news
   * Uses FormData for file upload
   */
  async updateNews(newsData: UpdateNewsRequest): Promise<ApiResponse<any>> {
    try {
      const formData = new FormData();
      formData.append('ID', newsData.ID.toString());
      formData.append('title', newsData.title);
      formData.append('newsDescription', newsData.newsDescription);

      if (newsData.imageFile) {
        formData.append('imageFile', newsData.imageFile);
      }

      const response = await api.put(ApiConstant.updateNews, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return {
        isSuccess: response.data.isSuccess,
        message: response.data.message,
        id: response.data.ID || response.data.id,
        ID: response.data.ID,
        data: response.data,
      };
    } catch (error: any) {
      console.error('Failed to update news:', error);
      throw error;
    }
  }

  //Swapping news 
  async updateNewsOrder(orderData: { Id: number; DisplayOrder: number }[]) {
  return api.post(ApiConstant.swapNewsOrder, orderData);
  }

  /**
   * Delete news
   */
  async deleteNews(newsId: number): Promise<ApiResponse<any>> {
    try {
      const response = await api.delete(ApiConstant.deleteNews, {
        params: { newsId },
      });

      return {
        isSuccess: response.data.isSuccess,
        message: response.data.message,
        id: response.data.ID || response.data.id,
        ID: response.data.ID,
        data: response.data,
      };
    } catch (error: any) {
      console.error('Failed to delete news:', error);
      throw error;
    }
  }
}

export default new NewsManagementService();
